import { useMemo, useState } from 'react';
import { useJobs } from '../lib/queries';
import { useWeatherPacket, useWeatherPull, useCompanyCamPush, type WeatherObservation, type WeatherPacket } from '../lib/weather';
import { Button, EmptyState, Input } from '../components/ui';
import { downloadPacketPdf, stationLine, stationSummary } from './weatherPdf';
import { cn, formatDateCompact, formatScheduleDate } from '../lib/utils';

interface DayRow {
    date: string;
    modelPrecip: number | null;
    noaaPrecip: number | null;
    high: number | null;
    low: number | null;
    rain: boolean;
}

// Pivot the per-source observations into one row per date.
function toDayRows(observations: WeatherObservation[]): DayRow[] {
    const m = new Map<string, { om?: WeatherObservation; noaa?: WeatherObservation }>();
    for (const o of observations) {
        const e = m.get(o.obs_date) ?? {};
        if (o.source === 'noaa') e.noaa = o;
        else e.om = o;
        m.set(o.obs_date, e);
    }
    return [...m.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, e]) => {
            const modelPrecip = e.om?.precip_in ?? null;
            const noaaPrecip = e.noaa?.precip_in ?? null;
            return {
                date,
                modelPrecip,
                noaaPrecip,
                high: e.noaa?.temp_max_f ?? e.om?.temp_max_f ?? null,
                low: e.noaa?.temp_min_f ?? e.om?.temp_min_f ?? null,
                rain: (modelPrecip ?? 0) > 0 || (noaaPrecip ?? 0) > 0,
            };
        });
}

function csvCell(value: string | number | null | undefined): string {
    const s = String(value ?? '').replace(/\r?\n/g, ' ').replace(/"/g, '""');
    return `"${s}"`;
}

function exportPacketCsv(packet: WeatherPacket, rows: DayRow[]) {
    const meta = [
        ['Builder', packet.builder],
        ['Community', packet.community],
        ['Lot', packet.lot_number],
        ['Address', packet.address ?? ''],
        ['Prep date', packet.prep_date ?? ''],
        ['Pour date', packet.pour_date ?? ''],
        ['Weather station', stationSummary(packet) ?? ''],
    ];
    const header = ['Date', 'Precip Model (in)', 'Precip NOAA (in)', 'High (F)', 'Low (F)'];
    const body = rows.map((r) =>
        [r.date, r.modelPrecip ?? '', r.noaaPrecip ?? '', r.high ?? '', r.low ?? ''].map(csvCell).join(',')
    );
    const lines = [
        ...meta.map((m) => m.map(csvCell).join(',')),
        '',
        header.map(csvCell).join(','),
        ...body,
        '',
        [`Model: Open-Meteo.com. ${stationLine(packet)}`].map(csvCell).join(','),
    ];
    const csv = lines.join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `weather-${packet.community.replace(/\s+/g, '-')}-lot-${packet.lot_number}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export function WeatherPage() {
    const { data: jobs, isLoading: jobsLoading } = useJobs();
    const [search, setSearch] = useState('');
    const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

    const matches = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return [];
        return (jobs ?? [])
            .filter((j) => {
                const hay = [j.lot_number, j.community.name, j.builder.name, j.address ?? '']
                    .join(' ')
                    .toLowerCase();
                return hay.includes(q);
            })
            .slice(0, 15);
    }, [jobs, search]);

    const { data: packet, isLoading: packetLoading, error: packetError } = useWeatherPacket(selectedJobId);
    const pullMut = useWeatherPull();

    function handlePull() {
        if (selectedJobId) pullMut.mutate(selectedJobId);
    }

    return (
        <div className="p-4 md:p-6 max-w-5xl mx-auto">
            <div className="mb-5">
                <h1 className="font-display font-bold text-xl text-graphite-900 dark:text-graphite-50">Weather</h1>
                <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-graphite-500 mt-1">
                    Prep-to-pour weather for builder EPO disputes
                </p>
            </div>

            {/* Lot search */}
            <div className="mb-6">
                <Input
                    placeholder="Search a lot by community, lot number or address..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                {search.trim() && matches.length > 0 ? (
                    <div className="mt-2 border border-graphite-200 dark:border-graphite-800 rounded divide-y divide-graphite-200 dark:divide-graphite-800 overflow-hidden">
                        {matches.map((j) => (
                            <button
                                key={j.id}
                                type="button"
                                onClick={() => {
                                    setSelectedJobId(j.id);
                                    setSearch('');
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-graphite-100 dark:hover:bg-graphite-900 transition-colors flex items-center gap-2"
                            >
                                <span className="font-mono text-xs text-graphite-500 tabular shrink-0">{j.builder.prefix}</span>
                                <span className="font-display font-medium text-graphite-900 dark:text-graphite-100">{j.community.name}</span>
                                <span className="font-mono text-xs text-graphite-600 dark:text-graphite-400 tabular">Lot {j.lot_number}</span>
                                {j.address ? <span className="text-xs text-graphite-500 truncate">· {j.address}</span> : null}
                            </button>
                        ))}
                    </div>
                ) : null}
                {search.trim() && !jobsLoading && matches.length === 0 ? (
                    <p className="mt-2 text-sm text-graphite-500">No lots match that search.</p>
                ) : null}
            </div>

            {/* Packet */}
            {!selectedJobId ? (
                <EmptyState
                    title="Pick a lot"
                    description="Search for a lot above to see its prep-to-pour weather window."
                />
            ) : packetLoading ? (
                <div className="font-mono text-xs text-graphite-500 uppercase tracking-widest">Loading...</div>
            ) : packetError ? (
                <div className="border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 px-4 py-3 rounded font-mono text-sm">
                    {(packetError as Error).message}
                </div>
            ) : packet ? (
                <PacketView packet={packet} onPull={handlePull} pulling={pullMut.isPending} pullError={pullMut.error as Error | null} />
            ) : null}
        </div>
    );
}

function PacketView({
    packet,
    onPull,
    pulling,
    pullError,
}: {
    packet: WeatherPacket;
    onPull: () => void;
    pulling: boolean;
    pullError: Error | null;
}) {
    const rows = useMemo(() => toDayRows(packet.observations), [packet.observations]);
    const hasWindow = packet.prep_date && packet.pour_date;
    const hasObs = rows.length > 0;
    const rainDays = rows.filter((r) => r.rain).length;
    const hasNoaa = rows.some((r) => r.noaaPrecip != null);
    const station = stationSummary(packet);

    const pushMut = useCompanyCamPush();
    const [pushMsg, setPushMsg] = useState<{ ok: boolean; text: string } | null>(null);

    async function handlePush() {
        setPushMsg(null);
        try {
            const res = await pushMut.mutateAsync({ packet, rows });
            if (res.uploaded && res.project) {
                setPushMsg({ ok: true, text: `Pushed to CompanyCam project ${res.project.name}.` });
            } else {
                setPushMsg({ ok: false, text: res.error ?? 'No matching CompanyCam project found.' });
            }
        } catch (e) {
            setPushMsg({ ok: false, text: e instanceof Error ? e.message : 'Push failed.' });
        }
    }

    return (
        <div className="border border-graphite-200 dark:border-graphite-800 rounded">
            {/* Header */}
            <div className="px-5 py-4 border-b border-graphite-200 dark:border-graphite-800">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="font-display font-semibold text-lg text-graphite-900 dark:text-graphite-50">
                            {packet.community} · Lot {packet.lot_number}
                        </div>
                        <div className="text-sm text-graphite-600 dark:text-graphite-400">
                            {packet.builder}
                            {packet.address ? ` · ${packet.address}` : ''}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        <Button variant="primary" size="sm" onClick={onPull} disabled={pulling || !hasWindow}>
                            {pulling ? 'Pulling...' : hasObs ? 'Refresh weather' : 'Pull weather'}
                        </Button>
                        <Button variant="secondary" size="sm" onClick={() => downloadPacketPdf(packet, rows)} disabled={!hasObs}>
                            Download PDF
                        </Button>
                        <Button variant="secondary" size="sm" onClick={handlePush} disabled={!hasObs || pushMut.isPending}>
                            {pushMut.isPending ? 'Pushing...' : 'Push to CompanyCam'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => exportPacketCsv(packet, rows)} disabled={!hasObs}>
                            CSV
                        </Button>
                    </div>
                </div>

                {pushMsg ? (
                    <div
                        className={cn(
                            'mt-3 border-l-2 px-3 py-2 text-sm',
                            pushMsg.ok
                                ? 'border-done-500 bg-done-500/10 text-done-700 dark:text-done-400'
                                : 'border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400'
                        )}
                    >
                        {pushMsg.text}
                    </div>
                ) : null}

                <div className="mt-3 flex flex-wrap gap-x-8 gap-y-1 font-mono text-xs">
                    <div>
                        <span className="text-graphite-500 uppercase tracking-wider">Prep </span>
                        <span className="text-graphite-900 dark:text-graphite-100 tabular">
                            {packet.prep_date ? formatScheduleDate(packet.prep_date) : '—'}
                        </span>
                    </div>
                    <div>
                        <span className="text-graphite-500 uppercase tracking-wider">Pour </span>
                        <span className="text-graphite-900 dark:text-graphite-100 tabular">
                            {packet.pour_date ? formatScheduleDate(packet.pour_date) : '—'}
                        </span>
                    </div>
                    {rainDays > 0 ? (
                        <div>
                            <span className="text-graphite-500 uppercase tracking-wider">Rain days </span>
                            <span className="text-signal-700 dark:text-signal-400 tabular">{rainDays}</span>
                        </div>
                    ) : null}
                    {/* Which station corroborated this lot, at a glance. A lot still
                        showing nothing here has not been pulled since per-site
                        station selection shipped. */}
                    {station ? (
                        <div>
                            <span className="text-graphite-500 uppercase tracking-wider">Station </span>
                            <span className="text-graphite-900 dark:text-graphite-100">{station}</span>
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Body */}
            <div className="px-5 py-4">
                {!hasWindow ? (
                    <p className="text-sm text-graphite-600 dark:text-graphite-400">
                        This lot has no completed prep and pour yet, so there is no window to pull. The weather pull
                        runs once both stages are marked complete.
                    </p>
                ) : !hasObs ? (
                    <p className="text-sm text-graphite-600 dark:text-graphite-400">
                        No weather pulled for this window yet. Hit Pull weather to fetch the daily record from prep
                        through pour.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="font-mono text-[10px] uppercase tracking-wider text-graphite-500 text-left">
                                    <th className="py-2 pr-4 font-normal">Date</th>
                                    <th className="py-2 pr-4 font-normal text-right">Precip Model (in)</th>
                                    <th className="py-2 pr-4 font-normal text-right">Precip NOAA (in)</th>
                                    <th className="py-2 pr-4 font-normal text-right">High</th>
                                    <th className="py-2 font-normal text-right">Low</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr
                                        key={r.date}
                                        className={cn(
                                            'border-t border-graphite-200 dark:border-graphite-900',
                                            r.rain && 'bg-signal-500/[0.06]'
                                        )}
                                    >
                                        <td className="py-2 pr-4 font-mono tabular text-graphite-900 dark:text-graphite-100">
                                            {formatDateCompact(r.date)}
                                        </td>
                                        <td
                                            className={cn(
                                                'py-2 pr-4 font-mono tabular text-right',
                                                (r.modelPrecip ?? 0) > 0
                                                    ? 'text-signal-700 dark:text-signal-400 font-semibold'
                                                    : 'text-graphite-700 dark:text-graphite-300'
                                            )}
                                        >
                                            {r.modelPrecip ?? '—'}
                                        </td>
                                        <td
                                            className={cn(
                                                'py-2 pr-4 font-mono tabular text-right',
                                                (r.noaaPrecip ?? 0) > 0
                                                    ? 'text-signal-700 dark:text-signal-400 font-semibold'
                                                    : 'text-graphite-700 dark:text-graphite-300'
                                            )}
                                        >
                                            {r.noaaPrecip ?? (hasNoaa ? '—' : 'n/a')}
                                        </td>
                                        <td className="py-2 pr-4 font-mono tabular text-right text-graphite-700 dark:text-graphite-300">
                                            {r.high ?? '—'}
                                        </td>
                                        <td className="py-2 font-mono tabular text-right text-graphite-700 dark:text-graphite-300">
                                            {r.low ?? '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="mt-3 font-mono text-[10px] text-graphite-400 dark:text-graphite-600 leading-relaxed">
                            Precip in inches. Model data by{' '}
                            <a href="https://open-meteo.com/" target="_blank" rel="noreferrer" className="underline">
                                Open-Meteo.com
                            </a>
                            . {stationLine(packet)}
                            {!hasNoaa ? ' NOAA values pending; the station may not have published this window yet.' : ''}
                        </p>
                    </div>
                )}

                {pullError ? (
                    <div className="mt-3 border-l-2 border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
                        {pullError.message}
                    </div>
                ) : null}
            </div>
        </div>
    );
}
